/**
 * FolderSelector Component
 * Provides folder selection with tree navigation
 */

import { useState, useMemo } from 'react'
import { ChevronRight, ChevronDown, Folder, FolderOpen, Home, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useContentFolderTree, useContentRootFolders } from '@/lib/hooks/use-content-hub'
import type { ContentFolderTree } from '@/lib/api/types'

// ============================================
// Types
// ============================================

interface FolderSelectorProps {
  value?: string | null
  onChange: (folderId: string | null) => void
  disabled?: boolean
  placeholder?: string
  className?: string
  excludeFolderId?: string // Exclude this folder and its children (for move operations)
}

interface FolderTreeNodeProps {
  folder: ContentFolderTree
  selectedId: string | null
  onSelect: (folderId: string) => void
  excludeFolderId?: string
  level?: number
}

// ============================================
// FolderTreeNode Component
// ============================================

function FolderTreeNode({
  folder,
  selectedId,
  onSelect,
  excludeFolderId,
  level = 0,
}: FolderTreeNodeProps) {
  const [isExpanded, setIsExpanded] = useState(level < 2) // Auto-expand first 2 levels
  const hasChildren = folder.children && folder.children.length > 0
  const isSelected = selectedId === folder.id
  const isExcluded = folder.id === excludeFolderId

  // Check if any descendant is selected to auto-expand
  const hasSelectedDescendant = useMemo(() => {
    const checkDescendants = (node: ContentFolderTree): boolean => {
      if (node.id === selectedId) return true
      return node.children?.some(checkDescendants) ?? false
    }
    return checkDescendants(folder)
  }, [folder, selectedId])

  // Auto-expand if descendant is selected
  useState(() => {
    if (hasSelectedDescendant) {
      setIsExpanded(true)
    }
  })

  if (isExcluded) {
    return null
  }

  return (
    <div>
      <div
        className={cn(
          'flex items-center gap-1 py-1.5 px-2 rounded-lg cursor-pointer transition-colors',
          isSelected
            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-900 dark:text-blue-100'
            : 'hover:bg-neutral-100 dark:hover:bg-neutral-800'
        )}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={() => onSelect(folder.id)}
      >
        {/* Expand/Collapse Button */}
        {hasChildren ? (
          <button
            type="button"
            className="p-0.5 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded transition-colors"
            onClick={(e) => {
              e.stopPropagation()
              setIsExpanded(!isExpanded)
            }}
          >
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5 text-neutral-500" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-neutral-500" />
            )}
          </button>
        ) : (
          <span className="w-5" /> // Spacer for alignment
        )}

        {/* Folder Icon */}
        {isExpanded && hasChildren ? (
          <FolderOpen className="h-4 w-4 text-amber-500 flex-shrink-0" />
        ) : (
          <Folder className="h-4 w-4 text-amber-500 flex-shrink-0" />
        )}

        {/* Folder Name */}
        <span className="text-sm truncate flex-1">{folder.name}</span>

        {/* Item Count */}
        {folder.itemCount > 0 && (
          <span className="text-xs text-neutral-500 dark:text-neutral-400">
            {folder.itemCount}
          </span>
        )}
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div>
          {folder.children
            .filter((child) => child.id !== excludeFolderId)
            .map((child) => (
              <FolderTreeNode
                key={child.id}
                folder={child}
                selectedId={selectedId}
                onSelect={onSelect}
                excludeFolderId={excludeFolderId}
                level={level + 1}
              />
            ))}
        </div>
      )}
    </div>
  )
}

// ============================================
// FolderSelector Component
// ============================================

export function FolderSelector({
  value,
  onChange,
  disabled = false,
  placeholder = 'Select folder (optional)',
  className,
  excludeFolderId,
}: FolderSelectorProps) {
  const [open, setOpen] = useState(false)

  // Fetch folder tree
  const { data: folderTree, isLoading } = useContentFolderTree()
  const { data: rootFolders } = useContentRootFolders()

  // Find selected folder name
  const findFolderName = (
    folders: ContentFolderTree[] | undefined,
    id: string
  ): string | null => {
    if (!folders) return null
    for (const folder of folders) {
      if (folder.id === id) return folder.name
      if (folder.children) {
        const found = findFolderName(folder.children, id)
        if (found) return found
      }
    }
    return null
  }

  const selectedFolderName = value ? findFolderName(folderTree, value) : null

  const handleSelect = (folderId: string | null) => {
    onChange(folderId)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-start rounded-xl font-normal',
            !value && 'text-neutral-500 dark:text-neutral-400',
            className
          )}
        >
          {value ? (
            <div className="flex items-center gap-2 truncate">
              <Folder className="h-4 w-4 text-amber-500 flex-shrink-0" />
              <span className="truncate">{selectedFolderName || 'Loading...'}</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Folder className="h-4 w-4 text-neutral-400 flex-shrink-0" />
              <span>{placeholder}</span>
            </div>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-80 p-0" align="start">
        <div className="p-2 border-b border-neutral-200 dark:border-neutral-700">
          <p className="text-sm font-medium text-neutral-900 dark:text-white">Select Folder</p>
        </div>

        <ScrollArea className="h-64">
          <div className="p-2">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
              </div>
            ) : (
              <>
                {/* Root Option */}
                <div
                  className={cn(
                    'flex items-center gap-2 py-1.5 px-2 rounded-lg cursor-pointer transition-colors',
                    !value
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-900 dark:text-blue-100'
                      : 'hover:bg-neutral-100 dark:hover:bg-neutral-800'
                  )}
                  onClick={() => handleSelect(null)}
                >
                  <Home className="h-4 w-4 text-neutral-500" />
                  <span className="text-sm">Root (No folder)</span>
                </div>

                {/* Folder Tree */}
                {folderTree && folderTree.length > 0 ? (
                  <div className="mt-2 pt-2 border-t border-neutral-200 dark:border-neutral-700">
                    {folderTree
                      .filter((folder) => folder.id !== excludeFolderId)
                      .map((folder) => (
                        <FolderTreeNode
                          key={folder.id}
                          folder={folder}
                          selectedId={value || null}
                          onSelect={handleSelect}
                          excludeFolderId={excludeFolderId}
                        />
                      ))}
                  </div>
                ) : rootFolders && rootFolders.length > 0 ? (
                  // Fallback to flat list if tree is not available
                  <div className="mt-2 pt-2 border-t border-neutral-200 dark:border-neutral-700">
                    {rootFolders
                      .filter((folder) => folder.id !== excludeFolderId)
                      .map((folder) => (
                        <div
                          key={folder.id}
                          className={cn(
                            'flex items-center gap-2 py-1.5 px-2 rounded-lg cursor-pointer transition-colors',
                            value === folder.id
                              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-900 dark:text-blue-100'
                              : 'hover:bg-neutral-100 dark:hover:bg-neutral-800'
                          )}
                          onClick={() => handleSelect(folder.id)}
                        >
                          <Folder className="h-4 w-4 text-amber-500" />
                          <span className="text-sm truncate">{folder.name}</span>
                          {folder.itemCount > 0 && (
                            <span className="text-xs text-neutral-500 dark:text-neutral-400 ml-auto">
                              {folder.itemCount}
                            </span>
                          )}
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-neutral-500 dark:text-neutral-400">
                    <Folder className="h-8 w-8 mx-auto mb-2 text-neutral-300 dark:text-neutral-600" />
                    <p className="text-sm">No folders created yet</p>
                  </div>
                )}
              </>
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  )
}

export default FolderSelector
